import { TLayoutItem } from '@redux/layouts/actions';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import RestList from 'containers/rest/List';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import I18n from 'i18next';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import { getColumns } from './columns';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import useGetSearchKey from 'hooks/useSearchKeys';
import { DATA_FIELD_SEARCH, ORDER_TTB_STATUS } from 'constants/ordersTabTryBuy';
import RangePickerFilter from '../components/Filter/RangePickerFilter';
import { useDispatch } from '@redux';
import crudActions from '@redux/crudActions';

const SEARCH_VALUE = {
  all: '',
  AWAITING_CONFIRMATION: ['AWAITING_CONFIRMATION'],
  CONFIRMED: ['CONFIRMED'],
  DELIVERING: ['DELIVERING'],
  READY_TO_TRY: ['READY_TO_TRY'],
  DELIVERY_SUCCESS: ['DELIVERY_SUCCESS'],
  CANCELLED: ['CANCELLED'],
};

const FILTERS_OBJECT = {
  AWAITING_CONFIRMATION: {
    condition: 'and',
    value: SEARCH_VALUE['AWAITING_CONFIRMATION'],
    operator: 'is',
    key: 'statusOms',
  },
  CONFIRMED: {
    condition: 'and',
    value: SEARCH_VALUE['CONFIRMED'],
    operator: 'is',
    key: 'statusOms',
  },
  DELIVERING: {
    condition: 'and',
    value: SEARCH_VALUE['DELIVERING'],
    operator: 'is',
    key: 'statusOms',
  },
  READY_TO_TRY: {
    condition: 'and',
    value: SEARCH_VALUE['READY_TO_TRY'],
    operator: 'is',
    key: 'statusOms',
  },
  DELIVERY_SUCCESS: {
    condition: 'and',
    value: SEARCH_VALUE['DELIVERY_SUCCESS'],
    operator: 'is',
    key: 'statusOms',
  },
  CANCELLED: {
    condition: 'and',
    value: SEARCH_VALUE['CANCELLED'],
    operator: 'is',
    key: 'statusOms',
  },
};

const INITIAL_LAYOUTS: TLayoutItem[] = [
  {
    rootPath: '/orders-keep-and-try',
    pathId: 'AWAITING_CONFIRMATION',
    title: 'Awaiting Confirmation',
    customPath: `/orders-keep-and-try?customLayout=AWAITING_CONFIRMATION&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['AWAITING_CONFIRMATION']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-keep-and-try',
    pathId: 'CONFIRMED',
    title: 'Confirmed',
    customPath: `/orders-keep-and-try?customLayout=CONFIRMED&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['CONFIRMED']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-keep-and-try',
    pathId: 'DELIVERING',
    title: 'Delivering',
    customPath: `/orders-keep-and-try?customLayout=DELIVERING&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['DELIVERING']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/orders-keep-and-try',
    pathId: 'READY_TO_TRY',
    title: 'Ready to try',
    customPath: `/orders-keep-and-try?customLayout=READY_TO_TRY&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['READY_TO_TRY']], null, 0),
    )}`,
    isDefault: true,
  },
  // {
  //   rootPath: '/orders-keep-and-try',
  //   pathId: 'COMPLETED',
  //   title: 'Completed',
  //   customPath: `/orders-keep-and-try?customLayout=COMPLETED&filters=${encodeURIComponent(
  //     JSON.stringify([FILTERS_OBJECT['COMPLETED']], null, 0),
  //   )}`,
  //   isDefault: true,
  // },
  // {
  //   rootPath: '/orders-keep-and-try',
  //   pathId: 'CANCELLED',
  //   title: 'Cancelled',
  //   customPath: `/orders-keep-and-try?customLayout=CANCELLED&filters=${encodeURIComponent(
  //     JSON.stringify([FILTERS_OBJECT['CANCELLED']], null, 0),
  //   )}`,
  //   isDefault: true,
  // },
];

const filters: TFilterItem[] = [
  {
    key: 'statusOms',
    label: 'Status',
    dataSource: Object.values(ORDER_TTB_STATUS)?.map(
      (e: DynamicObjectType) => ({
        value: e.value,
        label: I18n.t(e.text),
      }),
    ),
  },
  // {
  //   key: 'newOrder.payments.status',
  //   label: 'Payment status',
  //   dataSource: Object.values(PAYMENT_STATUS_CONST).map(
  //     (e: DynamicObjectType) => ({
  //       value: e.value,
  //       label: I18n.t(e.text),
  //     }),
  //   ),
  // },
  // {
  //   key: 'newOrder.paymentType',
  //   label: 'Payment method',
  //   valueCPN: <PaymentMethodSelect />,
  // },
  // {
  //   key: 'orderReturn.returnType',
  //   label: 'Exchange type',
  //   dataSource: Object.values(EXCHANGE_CHANNEL),
  // },
  {
    key: 'createdAt',
    label: 'Created time',
    isDatePicker: true,
  },
  {
    key: 'cancelTime',
    label: 'Canceled time',
    isDatePicker: true,
  },
  {
    key: 'confirmationTime',
    label: 'Confirmed time',
    isDatePicker: true,
  },
  {
    key: 'handedOverTime',
    label: 'Delivering time',
    isDatePicker: true,
  },
  {
    key: 'readyToTryTime',
    label: 'Ready to try time',
    isDatePicker: true,
  },
  {
    key: 'deliveryCompletedTime',
    label: 'Completed time',
    isDatePicker: true,
  },
];

const List = ({ initialFilter }: { initialFilter?: DynamicObjectType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { searchParams } = useGetFilter();
  const { searchKeys } = useGetSearchKey(DATA_FIELD_SEARCH);

  const restInitialFilter: FilterFromUrlResponseType = useMemo(
    () => ({
      join: ['customer', 'orderItems', 'transferOrders'],
      orderBy: 'createdAt,ASC',
      filters: [
        ...(initialFilter ? [initialFilter] : []),
        {
          condition: 'and',
          key: 'orderType',
          operator: 'is',
          value: 'TAB_TRY_BUY',
        },
        searchParams.get('statusOms')
          ? {
              condition: 'and',
              key: 'statusOms',
              operator: 'is',
              value: SEARCH_VALUE[searchParams.get('statusOms')],
            }
          : {},
      ],
    }),
    [initialFilter, searchParams],
  );

  const _columns = useMemo(() => {
    return getColumns(searchParams.get('customLayout')) as IRestColumnProps[];
  }, [searchParams.get('customLayout'), searchParams.get('statusOms')]);

  useEffect(() => {
    dispatch(crudActions['ordersTabTryBuy'].clearCurrent());
    dispatch(crudActions['orderHistories'].resetData());
  }, [window.location.pathname]);

  return (
    <RestList
      initialFilter={restInitialFilter}
      columns={_columns}
      model="ordersTabTryBuy"
      filters={filters.filter((item) => !!item?.key)}
      gotoCreatePage={() => navigate('/orders-keep-and-try/create')}
      searchKeys={searchKeys}
      extraActionFilters={<RangePickerFilter />}
      placeholderInputSearch="Search by Order ID, SO, Shipping, Phone, Email,..."
      rowSelectionActions={<></>}
      hasImport={false}
      hasExport={false}
      hasCustomLayout
      initialLayouts={INITIAL_LAYOUTS}
      dataFieldSearch={DATA_FIELD_SEARCH}
    />
  );
};

export default List;
